import {
	type SurfaceInitializationOptions,
	type SurfaceState,
	type SurfaceStateWithUpdateId,
} from '../../types';
import { createUpdateId } from '../../utils/updateId';

import { type Subscriber, type SurfaceStates, type SurfaceStore, type Unsubscribe } from './types';

const transformSurfaceInitToSurfaceState = (
	name: string,
	initOptions: SurfaceInitializationOptions,
): Omit<SurfaceState, 'surface' | 'isLoading'> => ({
	name,
	error: undefined,
	fallback: initOptions?.fallback,
});

const transformSurfaceInitializationToClientSurfaceStates = <SurfaceName extends string>(
	surfaces: Record<SurfaceName, SurfaceInitializationOptions>,
) =>
	new Map(
		Object.entries<SurfaceInitializationOptions>(surfaces).map<
			[string, Omit<SurfaceState, 'surface' | 'isLoading' | 'updateId'>]
		>(([name, initOptions]) => [name, transformSurfaceInitToSurfaceState(name, initOptions)]),
	);

const transformSurfaceInitializationToSurfaceByCombinedKey = <SurfaceName extends string>(
	surfaces: Record<SurfaceName, SurfaceInitializationOptions>,
) =>
	new Map(
		Object.entries(
			Object.entries<SurfaceInitializationOptions>(surfaces).reduce<
				Record<string, Pick<SurfaceStateWithUpdateId, 'surface' | 'isLoading' | 'updateId'>>
			>((a, [, initOptions]) => {
				const surfaceKey = initOptions.surfaceKey ?? initOptions.surface?.key;
				const surfaceType = initOptions.surfaceType ?? initOptions.surface?.type;
				const combinedKey = buildCombinedKey(surfaceType!, surfaceKey!);
				const updateId = createUpdateId();
				return {
					...a,
					[combinedKey]: {
						updateId,
						surface: a?.[combinedKey]?.surface || initOptions?.surface,
						isLoading: !(a?.[combinedKey]?.surface || initOptions.load === 'PRE'),
					},
				};
			}, {}),
		),
	);

export const transformSurfaceInitializationToCombinedKeyByName = <SurfaceName extends string>(
	surfaces: Record<SurfaceName, SurfaceInitializationOptions>,
) =>
	new Map<SurfaceName, string>(
		Object.entries<SurfaceInitializationOptions>(surfaces).map<[SurfaceName, string]>(
			([name, initOptions]) => [
				name as SurfaceName,
				buildCombinedKey(
					(initOptions.surfaceType ?? initOptions.surface?.type)!,
					(initOptions.surfaceKey ?? initOptions.surface?.key)!,
				),
			],
		),
	);

const createSurfaceStates = <SurfaceName extends string>(
	surfaces: Record<SurfaceName, SurfaceInitializationOptions>,
) => {
	const clientSurfaceStates = transformSurfaceInitializationToClientSurfaceStates(surfaces);
	const surfaceCombinedKeyByName = transformSurfaceInitializationToCombinedKeyByName(surfaces);
	const surfacesByCombinedKey = transformSurfaceInitializationToSurfaceByCombinedKey(surfaces);

	return {
		set: (name: SurfaceName, state: SurfaceState) => {
			const surfaceCombinedKey = surfaceCombinedKeyByName.get(name);
			const updateId = createUpdateId();

			if (
				!surfaceCombinedKey ||
				!clientSurfaceStates.has(name) ||
				!surfacesByCombinedKey.has(surfaceCombinedKey)
			) {
				throw Error(
					`Tried to update a surface with name ${name}, but no surface of that name exists. Check your initialization settings`,
				);
			}

			clientSurfaceStates.set(name, {
				name,
				fallback: state.fallback,
				error: state.error,
			});
			surfacesByCombinedKey.set(surfaceCombinedKey, {
				updateId,
				surface: state.surface,
				isLoading: state.isLoading,
			});
		},
		get: (name: SurfaceName) => {
			try {
				const surfaceCombinedKey = surfaceCombinedKeyByName.get(name);
				const surfaceState = clientSurfaceStates.get(name);
				const surfaceByCombinedKey = surfacesByCombinedKey.get(surfaceCombinedKey as string);

				if (!surfaceState || !surfaceByCombinedKey) {
					throw Error;
				}

				return { ...surfaceState, ...surfaceByCombinedKey };
			} catch {
				throw Error(
					`Tried to get surface with name "${name}" but no surface of that name was initialized`,
				);
			}
		},
		isSelected: (name: SurfaceName, selectors: string[]): boolean =>
			!!selectors.find(
				(selector) =>
					surfaceCombinedKeyByName.get(selector as SurfaceName) ===
					surfaceCombinedKeyByName.get(name),
			),
	};
};

export const createSurfaceStore = <SurfaceName extends string>(
	surfaceInitializationOptions: Record<SurfaceName, SurfaceInitializationOptions>,
): SurfaceStore<SurfaceName> => {
	let surfaceStates = createSurfaceStates(surfaceInitializationOptions);
	const subscribers = new Map<Subscriber, Subscriber>();

	const updateSubscribers = ({ name }: SurfaceState): void => {
		const surfaceName = name as SurfaceName;

		subscribers.forEach((subscriber) => updateSubscriber(subscriber, surfaceName));
	};

	const updateSubscriber = (subscriber: Subscriber, surfaceName: SurfaceName): void => {
		const [subscription, selectors] = subscriber;

		if (!selectors || surfaceStates.isSelected(surfaceName, selectors)) {
			const state = surfaceStates.get(surfaceName);

			if (state) {
				subscription(state);
			}
		}
	};

	const subscribe = (...subscriber: Subscriber): Unsubscribe => {
		const [subscription, selectors, options] = subscriber;

		subscribers.set(subscriber, subscriber);

		if (options?.updateOnSubscribe) {
			selectors?.forEach((selector) => {
				updateSubscriber([subscription, [selector]], selector as SurfaceName);
			});
		}

		return () => subscribers.delete(subscriber);
	};

	const setState = (newSurfaceState: SurfaceState, callback: () => void): void => {
		const surfaceName = newSurfaceState.name as SurfaceName;

		surfaceStates.set(surfaceName, newSurfaceState);

		callback();
	};

	const update = (
		surfaceName: SurfaceName,
		surfaceState:
			| Partial<SurfaceState>
			| ((previousSurfaceState: SurfaceState) => Partial<SurfaceState>),
	): void => {
		const previousSurfaceState = surfaceStates.get(surfaceName);

		const partialSurfaceState =
			typeof surfaceState === 'function'
				? surfaceState(surfaceStates.get(surfaceName))
				: surfaceState;

		const newSurfaceState = { ...previousSurfaceState, ...partialSurfaceState };

		setState(newSurfaceState, () => {
			updateSubscribers(newSurfaceState);
		});
	};

	const getCurrentSurfaceState = (
		surfaceName: SurfaceName,
	): NonNullable<SurfaceStates<SurfaceName>>[SurfaceName] => surfaceStates.get(surfaceName);

	const getCurrentSurfaceStateFromKeys = (
		surfaceType: string,
		surfaceKey: string,
	): NonNullable<SurfaceStates<SurfaceName>>[SurfaceName] =>
		surfaceStates.get(buildCombinedKey(surfaceType, surfaceKey) as SurfaceName);

	const getSurfaceInitializationOptions = (
		surfaceName: SurfaceName,
	): SurfaceInitializationOptions => {
		const surfaceOptions = surfaceInitializationOptions?.[surfaceName];

		if (!surfaceOptions) {
			throw Error(
				`Tried to get surface initializations options for "${surfaceName}" but no surface of that name was initialized`,
			);
		}

		return surfaceOptions;
	};

	const getSurfaces = () => {
		return surfaceInitializationOptions;
	};

	return {
		getSurfaceInitializationOptions,
		getCurrentSurfaceState,
		getCurrentSurfaceStateFromKeys,
		getSurfaces,
		update,
		subscribe,
	};
};

function buildCombinedKey(SurfaceType: string, surfaceKey: string): string {
	return `${SurfaceType},${surfaceKey}`;
}
